/// <amd-module name="typeahead/core/ExpediaSuggestServiceRequestGenerator" />
/// <amd-dependency path="jquery" name="$"/>

/// <reference path="../../typings/typeahead/browser.d.ts" />
/// <reference path="../../typings/typeahead/modulizr.d.ts" />
/// <reference path="../../typings/typeahead/window.d.ts" />
/// <reference path="../../typings/typeahead/plugins.d.ts" />
/// <reference path="../../typings/typeahead/ExpediaSuggestServiceRequestGenerator.d.ts" />

"use strict";
import {RequestGenerator} from "typeahead/core/RequestGenerator";

export interface IInputElementConfig {
    inputElementId: string;
    searchRegionId: string;
}

function getOptionalServiceUrl(options: any, srchText: string): string {
    return [
        options.additionalDataSourceUrl,
        encodeURIComponent(srchText),
    ].join("");
}

function getQueryParams(requestParams: ISuggestServiceQueryParams, context: IContext): any {
    let urlParams: any = {};

    // I think it makes sense to split the list of parameters into 2 groups
    // - parameters come from the context
    // - parameters come from the request params object that may or may not have features/tests

    // parameters from the context
    urlParams.client = context.clientId;
    urlParams.siteid = context.siteid;
    urlParams.guid = context.guid;
    urlParams.lob = context.lob;
    urlParams.locale = context.locale;
    urlParams.expuserid = context.expuserid;

    // parameters from the request params object
    urlParams.regiontype = requestParams.mask;
    urlParams.ab = requestParams.ab;
    urlParams.dest = requestParams.dest;
    urlParams.maxresults = requestParams.maxresults;
    urlParams.features = requestParams.features.join("|");
    urlParams.rid = requestParams.rid;
    urlParams.sortcriteria = requestParams.sortcriteria;
    urlParams.categories = requestParams.categories;
    urlParams.personalize = requestParams.personalize;
    urlParams.pt = requestParams.packageType;

    // others
    urlParams.format = "jsonp";
    urlParams.device = requestParams.device;
    urlParams.browser = requestParams.browser;
    return urlParams;
}

function constructRequestParams(options: any): ISuggestServiceQueryParams {
    let requestParams: ISuggestServiceQueryParams = <ISuggestServiceQueryParams> {};

    requestParams.mask = options.mask;
    requestParams.ab = options.abtest;
    requestParams.format = options.format;
    requestParams.dest = options.dest;
    requestParams.maxresults = options.maxresults;
    requestParams.features = options.features || [];
    // device_type is available little late after pageload
    requestParams.device = window.utag_data ? window.utag_data.device_type : "Unavailable";
    requestParams.browser = options.browser;
    requestParams.rid = options.filter_region_id;
    requestParams.personalize = options.personalize;
    requestParams.packageType = options.packageType;

    return requestParams;
}

function applyRequestParamModifiers(
         plugins: IRequestUrlParamModifier[],
         context: IContext,
         parameters: ISuggestServiceQueryParams,
         options: IInputElementConfig): JQueryPromise<ISuggestServiceQueryParams> {
    let deferred: JQueryDeferred<ISuggestServiceQueryParams> = $.Deferred();
    if (plugins.length > 0) {
        let modifiedParameterPromises: Array<JQueryPromise<ISuggestServiceQueryParams>> =
            plugins.map((plugin: IRequestUrlParamModifier) => {
                return plugin.modifyParameters(parameters, context, options);
        });
        $.when.apply($, modifiedParameterPromises).done(() => {
            // we can just resolve
            deferred.resolve(parameters);
        });
    } else {
        deferred.resolve(parameters);
    }
    return deferred.promise();
}

export class ExpediaSuggestServiceRequestGenerator extends RequestGenerator {

    constructor(essAbacusIds: string[], variantAssigner: (id?: string) => string,
                suggestServiceUrlOptions: ISuggestServiceUrlOptions, siteid: string, pluginAssembly: IPluginAssembly) {
        super(essAbacusIds, variantAssigner, suggestServiceUrlOptions, siteid, pluginAssembly);
    }

    public makeRequest(srchText: string, options: any, context: IContext): JQueryPromise<any> {
        let url;
        if (options.isSecondary) {
            url = getOptionalServiceUrl(options, srchText);
        } else {
            url = this.constructEssUrl(srchText);
        }
        let requestParams: ISuggestServiceQueryParams = constructRequestParams(options);
        let inputOptions: IInputElementConfig = <IInputElementConfig> $.extend({}, options);
        return applyRequestParamModifiers(this.requestParamModifiers, context, requestParams, inputOptions)
            .then((modifiedParams) => {
                return $.getJSON(url, getQueryParams(modifiedParams, context));
            }); // this is a promise that can be chain
    }
}

/**
 * Function to allow the caller to provide the version of jQuery they want to use
 * and not rely on the page load time AMD module loader to provide one. This facilitates
 * unit testing so you can mock/stub/fake the jQuery functionality used in here.
 * @param jQuery
 */
export function overrideJQuery(jQuery: JQueryStatic): void {
    $ = jQuery;
}
